import React, { useState } from 'react';
import './LocationSection.css';
import { getCurrentLocationWithAddress, getCoordinatesFromAddress, LocationData } from '../utils/locationService';

interface LocationSectionProps {
  onLocationUpdate?: (location: LocationData) => void;
}

const LocationSection: React.FC<LocationSectionProps> = ({ onLocationUpdate }) => {
  const [selectedLocation, setSelectedLocation] = useState<'current' | 'manual'>('current');
  const [currentAddress, setCurrentAddress] = useState('서울시 강남구 테헤란로 123');
  const [manualAddress, setManualAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationData, setLocationData] = useState<LocationData | null>(null);

  const handleLocationTypeChange = async (type: 'current' | 'manual') => {
    setSelectedLocation(type);
    
    if (type === 'current') {
      await getCurrentLocation();
    }
  };

  const getCurrentLocation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const location = await getCurrentLocationWithAddress();
      setLocationData(location);
      setCurrentAddress(location.address || '주소를 가져올 수 없습니다.');
      
      // 부모 컴포넌트에 위치 정보 전달
      if (onLocationUpdate) {
        onLocationUpdate(location);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '위치 정보를 가져오는데 실패했습니다.';
      setError(errorMessage);
      setCurrentAddress('위치 정보를 가져올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualAddressSearch = async () => {
    if (!manualAddress.trim()) {
      setError('주소를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const location = await getCoordinatesFromAddress(manualAddress);
      setLocationData(location);
      setCurrentAddress(location.address || manualAddress);
      
      // 부모 컴포넌트에 위치 정보 전달
      if (onLocationUpdate) {
        onLocationUpdate(location);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '주소 검색에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualAddressKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleManualAddressSearch();
    }
  };

  return (
    <div className="location-section">
      <h3 className="section-title">📍 위치 설정</h3>
      
      <div className="location-buttons">
        <button 
          className={`location-btn ${selectedLocation === 'current' ? 'active' : ''}`}
          onClick={() => handleLocationTypeChange('current')}
          disabled={isLoading}
        >
          {isLoading ? '위치 확인 중...' : '현재 위치 사용'}
        </button>
        <button 
          className={`location-btn ${selectedLocation === 'manual' ? 'active' : ''}`}
          onClick={() => handleLocationTypeChange('manual')}
          disabled={isLoading}
        >
          주소 직접 입력
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* 주소 직접 입력 필드 */}
      {selectedLocation === 'manual' && (
        <div className="manual-address-input">
          <div className="input-group">
            <input
              type="text"
              value={manualAddress}
              onChange={(e) => setManualAddress(e.target.value)}
              onKeyPress={handleManualAddressKeyPress}
              placeholder="주소나 장소명 입력 (예: 강남역, 서울시청)"
              className="address-input"
              disabled={isLoading}
            />
            <button 
              className="search-btn"
              onClick={handleManualAddressSearch}
              disabled={isLoading || !manualAddress.trim()}
            >
              {isLoading ? '검색 중...' : '검색'}
            </button>
          </div>
          <div className="search-help-text">
            💡 전체 주소 또는 장소명으로 검색 가능합니다
          </div>
        </div>
      )}
      
      <div className="current-location">
        <p>📍 {currentAddress}</p>
        {locationData && (
          <div className="location-details">
            <small>위도: {locationData.latitude.toFixed(6)}</small>
            <small>경도: {locationData.longitude.toFixed(6)}</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSection;